<script setup lang="ts">
/**
 * Builder.vue - Wrapper Component with Template Lazy Loading
 *
 * Este componente actúa como un contenedor que:
 * 1. Inicializa el BuilderCore (toda la lógica de negocio)
 * 2. Carga dinámicamente el template apropiado según URL params o config
 * 3. Pasa el core al template para renderizado
 *
 * Los templates disponibles son:
 * - default: Template estándar con todas las funcionalidades
 * - viaggio: Template simplificado con branding de Viaggio Resort
 */

import { onMounted, ref } from 'vue';
import { createBuilderCore } from './BuilderCore';
import { useTemplateLoader } from '../composables/useTemplateLoader';

// Import Google Material Icons (needed by default template)
if (typeof document !== 'undefined') {
    const existingLink = document.querySelector('link[href*="Material+Icons"]');
    if (!existingLink) {
        const link = document.createElement('link');
        link.href = 'https://fonts.googleapis.com/icon?family=Material+Icons';
        link.rel = 'stylesheet';
        document.head.appendChild(link);
    }
}

// Initialize core logic
const core = createBuilderCore();

// Template loader
const {
    currentTemplate,
    isLoadingTemplate,
    templateError,
    loadedTemplateName,
    loadBuilderTemplate
} = useTemplateLoader();

// Extract template name from URL query params or default to 'default'
const getTemplateFromUrl = (): string => {
    if (typeof window === 'undefined') return 'default';

    const urlParams = new URLSearchParams(window.location.search);
    const template = urlParams.get('template');

    // Valid templates: default, viaggio
    if (template && ['default', 'viaggio'].includes(template)) {
        return template;
    }

    return 'default';
};

// Current template name for display
const currentTemplateName = ref('');

// Load template on mount
onMounted(async () => {
    const templateName = getTemplateFromUrl();
    currentTemplateName.value = templateName;
    await loadBuilderTemplate(templateName);

    // If viaggio template, pre-fill slug
    if (templateName === 'viaggio' && !core.slug.value) {
        core.slug.value = 'viaggio-resort';
    }
});
</script>

<template>
    <!-- Loading State -->
    <div v-if="isLoadingTemplate" class="min-h-screen flex items-center justify-center bg-gray-100">
        <div class="text-center">
            <div class="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
            <p class="text-gray-600">Cargando...</p>
        </div>
    </div>

    <!-- Error State -->
    <div v-else-if="templateError" class="min-h-screen flex items-center justify-center bg-gray-100">
        <div class="text-center p-8 bg-white rounded-lg shadow-md max-w-md">
            <div class="text-red-500 mb-4">
                <svg class="w-16 h-16 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                        d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
            </div>
            <h2 class="text-xl font-bold text-gray-800 mb-2">Error al cargar el template</h2>
            <p class="text-gray-600 mb-4">{{ templateError }}</p>
            <button @click="loadBuilderTemplate('default')"
                class="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors">
                Cargar template por defecto
            </button>
        </div>
    </div>

    <!-- Template loaded successfully -->
    <component v-else-if="currentTemplate" :is="currentTemplate" :core="core" />
</template>
